import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";

const images = require.context(
  "../../assets/images/EXEpartners",
  false,
  /\.(png|jpe?g|svg|webp)$/i
);

// Dynamically load image with correct extension
const getImageWithCorrectExtension = (partnerName) => {
  const validExtensions = ["png", "jpeg", "jpg", "svg", "webp"];
  for (let ext of validExtensions) {
    try {
      const image = images(`./${partnerName}.${ext}`);
      if (image) return image;
    } catch (e) {
      continue;
    }
  }
  return images("./fallback.png");
};

const partners = [
  {
    name: "Graffon",
    title: "Graffon",
    link: "https://graffon.lk/",
    partnerType: "Strategic",
  },
  {
    name: "lenovo",
    title: "Lenovo",
    link: "https://lenovo.com/",
    partnerType: "Title",
  },
  {
    name: "LaptopLK",
    title: "LaptopLK",
    link: "https://laptop.lk/",
    partnerType: "Title",
  },
  {
    name: "spaceTimer",
    title: "Space Timer",
    link: "https://space-timer.com/",
    partnerType: "Game Design Award",
  },
  {
    name: "rivertune",
    title: "Rivertune",
    link: "https://www.linkedin.com/company/rivertune-games/about/",
    partnerType: "Curation",
  },
  {
    name: "ram",
    title: "RAM Studios",
    link: "https://www.linkedin.com/company/ramstudiossl/about/",
    partnerType: "Curation",
  },
  {
    name: "heladev",
    title: "HelaDev",
    link: "https://www.facebook.com/heladev/",
    partnerType: "Curation",
  },
  {
    name: "timelesstoucan",
    title: "Timeless Toucan",
    link: "https://www.timelesstoucan.com/",
    partnerType: "Knowledge",
  },
  {
    name: "Behaviol",
    title: "Behaviol",
    link: "https://meta11.com/",
    partnerType: "Knowledge",
  },
  {
    name: "BlackMirageStudio",
    title: "Black Mirage Studio",
    link: "https://www.linkedin.com/company/black-mirage-studios/about/",
    partnerType: "Creative Design",
  },
  {
    name: "FriendlyGameDev",
    title: "Friendly GameDev",
    link: "https://www.linkedin.com/company/friendly-gamedev/",
    partnerType: "Knowledge",
  },
  {
    name: "ISCS",
    title: "ISCS",
    link: "https://www.instagram.com/iscs.lk/",
    partnerType: "Scholarship Award Sponsor",
  },
  {
    name: "Yunash",
    title: "Yunash Gaming",
    link: "https://www.facebook.com/yunash.games/",
    partnerType: "Knowledge",
  },
  {
    name: "GoogleDevelopersGroupSriLanka",
    title: "GDG Sri Lanka",
    link: "https://gdg.community.dev/gdg-sri-lanka/",
    partnerType: "Community Outreach",
  },
  {
    name: "yashoda",
    title: "Yashoda Opticians",
    link: "https://maps.app.goo.gl/QoPPwQZ3v4B5Fxte7",
    partnerType: "Gift",
  },
  {
    name: "Codechefs",
    title: "Code Chefs",
    link: "https://www.linkedin.com/company/codechefs123/about/",
    partnerType: "Digital Media",
  },
  {
    name: "Chokolaate",
    title: "Chokolaate",
    link: "https://www.youtube.com/c/chokolaatemagazine",
    partnerType: "Digital Media",
  },
  {
    name: "GenAlpha",
    title: "Generation Alpha",
    link: "https://www.linkedin.com/company/generation-alpha2025/about/",
    partnerType: "Community Outreach",
  },
  {
    name: "checkmateAlley",
    title: "Checkmate Alley",
    link: "https://www.checkmatealley.com/",
    partnerType: "Community Outreach",
  },
  {
    name: "rakaara",
    title: "Rakaara",
    link: "#",
    partnerType: "Apparel",
  },
  {
    name: "CynthSpace",
    title: "Cynth Space",
    link: "#",
    partnerType: "Creative Design",
  },
  {
    name: "GoodPR",
    title: "Good PR",
    link: "https://www.thegoodpr.com/",
    partnerType: "Digital Media",
  },
];

const cardWidth = 250; // Card width for desktop
const mobileCardWidth = 200; // Smaller card width for mobile
const cardGap = 60; // Gap for better spacing
const mobileCardGap = 40; // Smaller gap for mobile
const visibleOffset = 2; // show focused + two on each side

const EXESponsors = () => {
  const [current, setCurrent] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const length = partners.length;
  const carouselRef = useRef(null);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auto-scroll every 4 seconds
  useEffect(() => {
    const id = setInterval(() => setCurrent((c) => c + 1), 4000);
    return () => clearInterval(id);
  }, []);

  // Normalize index within [0, length)
  const indexInRange = ((current % length) + length) % length;

  // Helper: compute wrapped offset for partner at i
  const computeOffset = (i) => {
    let raw = i - indexInRange;
    const half = Math.floor(length / 2);
    if (raw > half) raw -= length;
    if (raw < -half) raw += length;
    return raw;
  };

  const handlePrev = () => {
    setCurrent((c) => c - 1);
  };

  const handleNext = () => {
    setCurrent((c) => c + 1);
  };

  // Get responsive values
  const currentCardWidth = isMobile ? mobileCardWidth : cardWidth;
  const currentCardGap = isMobile ? mobileCardGap : cardGap;
  const cardHeight = isMobile ? 280 : 350;
  const logoHeight = isMobile ? 120 : 180;

  return (
    <section className="exe-section">
      <div className="exe-container px-8">
        <div
          ref={carouselRef}
          className="relative flex flex-col items-center justify-center w-full"
          style={{ perspective: isMobile ? 1000 : 1500 }}
        >
          <motion.h2
            className="exe-title text-exe-text-accent mb-6 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.3,
              ease: "easeOut",
              delay: 0.06,
            }}
            viewport={{ once: true }}
          >
            Event Sponsors & Partners
          </motion.h2>
          <div className="h-1 w-24 bg-exe-gradient-primary mx-auto mb-8"></div>

          {/* Carousel Container - Centered between arrows */}
          <div className="relative w-full flex items-center justify-center">
            {/* Left Arrow */}
            <button
              onClick={handlePrev}
              className="absolute left-2 md:left-4 lg:left-8 top-1/2 transform -translate-y-1/2 z-30 text-2xl md:text-4xl lg:text-5xl text-exe-primary hover:text-exe-text-primary transition-colors duration-150 hover:scale-110"
              style={{ transform: "translateY(-50%) translateZ(0)" }}
            >
              {"<"}
            </button>

            {/* Cards Container */}
            <div
              className="relative flex items-center justify-center"
              style={{
                width: isMobile ? "300px" : "800px",
                height: isMobile ? "320px" : "400px",
              }}
            >
              {partners.map((partner, i) => {
                const offset = computeOffset(i);

                // Only show offsets within [-visibleOffset, visibleOffset]
                if (Math.abs(offset) > visibleOffset) return null;

                const isFocused = offset === 0;

                // Enhanced rotation for better edge visibility
                const rotateY = isFocused
                  ? 0
                  : offset < 0
                  ? isMobile
                    ? -60
                    : -75
                  : isMobile
                  ? 60
                  : 75;

                // Adjust translateX for centering
                const translateX =
                  offset * (currentCardWidth * 0.3 + currentCardGap);

                // Different scales and positions for focused vs unfocused
                const scale = isFocused ? 1 : isMobile ? 0.6 : 0.7;
                const translateZ = isFocused
                  ? isMobile
                    ? 30
                    : 50
                  : isMobile
                  ? -10
                  : -20;

                return (
                  <div
                    key={i}
                    className="absolute top-1/2 left-1/2"
                    style={{
                      zIndex: isFocused ? 10 : 5 - Math.abs(offset),
                      transform: `
                        translate(-50%, -50%)
                        translateX(${translateX}px)
                        translateZ(${translateZ}px)
                        rotateY(${rotateY}deg)
                        scale(${scale})
                      `,
                      transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    <a
                      href={partner.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`
                        block rounded-2xl overflow-hidden cursor-pointer
                        transition-all duration-200 transform-gpu
                        ${
                          isFocused
                            ? "exe-elevated-card shadow-exe-glow border-5 border-white"
                            : "bg-exe-bg-card bg-opacity-50 border border-exe-border-primary"
                        }
                      `}
                      style={{
                        width: `${currentCardWidth}px`,
                        height: `${cardHeight}px`,
                      }}
                    >
                      <div className="w-full h-full flex flex-col items-center justify-center p-3 md:p-4">
                        {/* Logo - Only visible when focused */}
                        <div
                          className={`w-full flex items-center justify-center mb-2 md:mb-4 transition-opacity duration-200 ${
                            isFocused ? "opacity-100" : "opacity-0"
                          }`}
                          style={{ height: `${logoHeight}px` }}
                        >
                          <img
                            src={getImageWithCorrectExtension(partner.name)}
                            alt={`${partner.title} logo`}
                            className="max-w-full max-h-full object-contain filter brightness-110 rounded-xl"
                          />
                        </div>

                        {/* Partner Title - Enhanced visibility when focused with responsive text */}
                        <h4
                          className={`
                            font-bold text-center mb-2 md:mb-3 transition-all duration-200 px-2
                            ${
                              isFocused
                                ? "exe-text-gradient opacity-100 transform scale-100"
                                : "opacity-0"
                            }
                            ${
                              isMobile
                                ? "text-sm leading-tight"
                                : partner.title.length > 20
                                ? "text-lg md:text-xl"
                                : "text-xl md:text-2xl"
                            }
                          `}
                          style={{
                            // Ensure text wraps properly on mobile
                            wordBreak: isMobile ? "break-word" : "normal",
                            hyphens: isMobile ? "auto" : "none",
                            lineHeight: isMobile ? "1.2" : "1.3",
                          }}
                        >
                          {partner.title}
                        </h4>

                        {/* Partner Type - Only visible when focused */}
                        <div
                          className={`transition-all duration-200 ${
                            isFocused
                              ? "opacity-100 transform translateY(0)"
                              : "opacity-0 transform translateY(4)"
                          }`}
                        >
                          <span
                            className={`
                              text-exe-primary border border-exe-primary rounded-full 
                              bg-exe-primary bg-opacity-10 backdrop-blur-sm text-center
                              ${
                                isMobile
                                  ? "text-xs py-1 px-2"
                                  : "text-sm py-2 px-4"
                              }
                            `}
                            style={{
                              display: "inline-block",
                              maxWidth: isMobile ? "160px" : "200px",
                              wordBreak: "break-word",
                              lineHeight: "1.2",
                            }}
                          >
                            {partner.partnerType || "TBD"}
                          </span>
                        </div>
                      </div>
                    </a>
                  </div>
                );
              })}
            </div>

            {/* Right Arrow */}
            <button
              onClick={handleNext}
              className="absolute right-2 md:right-4 lg:right-8 top-1/2 transform -translate-y-1/2 z-30 text-2xl md:text-4xl lg:text-5xl text-exe-primary hover:text-exe-text-primary transition-colors duration-150 hover:scale-110"
              style={{ transform: "translateY(-50%) translateZ(0)" }}
            >
              {">"}
            </button>
          </div>

          {/* Dot Trail */}
          <motion.div
            className="flex space-x-2 md:space-x-3 mt-6 md:mt-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.36, duration: 0.3 }}
            viewport={{ once: true }}
          >
            {partners.map((_, idx) => (
              <motion.button
                key={idx}
                onClick={() => setCurrent(idx)}
                className={`block rounded-full transition-all duration-150 cursor-pointer ${
                  idx === indexInRange
                    ? `${
                        isMobile ? "w-3 h-3" : "w-4 h-4"
                      } bg-exe-primary shadow-exe-glow`
                    : `${
                        isMobile ? "w-2 h-2" : "w-3 h-3"
                      } bg-exe-text-muted hover:bg-exe-text-secondary`
                }`}
                whileHover={{ scale: 1.25 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </motion.div>

          {/* Partnership CTA */}
          <div className="text-center mt-8 md:mt-12 max-w-4xl px-4">
            <motion.h4
              className="exe-text text-xl exe-text-gradient mb-3 md:mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.18, duration: 0.3 }}
              viewport={{ once: true }}
            >
              Become A Partner With Us...
            </motion.h4>
            <motion.p
              className="exe-text leading-relaxed"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.24, duration: 0.3 }}
              viewport={{ once: true }}
            >
              Contact us at{" "}
              <a
                href="mailto:levelstair@gmail.com"
                className="text-exe-primary hover:text-exe-text-primary transition-all duration-150 relative inline-block font-semibold group"
              >
                levelstair@gmail.com
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-exe-primary transition-all duration-150 group-hover:w-full"></span>
              </a>
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EXESponsors;
